import { LRUObjectCache } from "common/utils/lruObjectCache";
import type { PolyLine2Polygon } from "../polyLine2Types";

/**
 * Byte-keyed memo cache for polyOp.
 *
 * polyOp is a pure function of its options: inputs are copied before mutation,
 * outputs are freshly allocated, and the DCEL never escapes. The memo key
 * encodes every option field plus the exact f64 bit patterns of every loop
 * coordinate, so a cache hit means "byte-identical input" and the stored
 * result is byte-identical to what a recompute would produce. Hits return
 * deep clones so callers can never mutate the stored master; masters are
 * plain Float64Arrays (not allocator-backed) so arena allocators, if ever
 * enabled, cannot corrupt the cache.
 *
 * The cache is a shared LRUObjectCache in identity mode (string keys) bounded by
 * both an entry count and a byte budget; this module only adds polyOp-specific
 * key construction and clone-on-read/write semantics.
 */

const CAPACITY = 8192;
// per-entry size is unbounded (cloned result scales with input), so cap retained
// bytes too; the heaviest known fixture retains ~2MB for two full option variants
const MAX_BYTES = 32 * 1024 * 1024;

function valueBytes(value: PolyLine2Polygon[]): number {
  let bytes = 0;
  for (const polygon of value) {
    for (const loop of polygon) {
      bytes += loop.byteLength;
    }
  }
  return bytes;
}

const cache = new LRUObjectCache<string, PolyLine2Polygon[]>(CAPACITY, undefined, undefined, {
  maxBytes: MAX_BYTES,
  sizeOf: valueBytes,
});

const OP_CODES = { union: 0, intersect: 1, subtract: 2 } as const;

// Reusable scratch buffer for key construction; grown geometrically as needed.
let keyF64 = new Float64Array(1024);
let keyU16 = new Uint16Array(keyF64.buffer);

function ensureKeyCapacity(doubles: number): void {
  if (doubles > keyF64.length) {
    let size = keyF64.length * 2;
    while (size < doubles) {
      size *= 2;
    }
    keyF64 = new Float64Array(size);
    keyU16 = new Uint16Array(keyF64.buffer);
  }
}

/**
 * Builds an injective string key: a fixed 7-double header followed by, per
 * polygon, its loop count and, per loop, its length and raw coordinates.
 * Inline lengths make the encoding prefix-unambiguous; encoding through
 * String.fromCharCode preserves arbitrary u16 values (including lone
 * surrogates), so distinct byte sequences always yield distinct strings.
 */
export function buildPolyOpMemoKey(
  pls: PolyLine2Polygon[],
  op: "union" | "intersect" | "subtract",
  roundingMode: number,
  reverse: boolean,
  mergeCoincidentVertexTol: number,
  mergeCollinear: boolean,
  snapRoundingTolerance: number,
): string {
  let total = 7;
  for (const polygon of pls) {
    total += 1 + polygon.length;
    for (const loop of polygon) {
      total += loop.length;
    }
  }
  ensureKeyCapacity(total);

  keyF64[0] = OP_CODES[op];
  keyF64[1] = roundingMode;
  keyF64[2] = reverse ? 1 : 0;
  keyF64[3] = mergeCoincidentVertexTol;
  keyF64[4] = mergeCollinear ? 1 : 0;
  keyF64[5] = snapRoundingTolerance;
  keyF64[6] = pls.length;

  let offset = 7;
  for (const polygon of pls) {
    keyF64[offset++] = polygon.length;
    for (const loop of polygon) {
      keyF64[offset++] = loop.length;
      keyF64.set(loop, offset);
      offset += loop.length;
    }
  }

  // chunk size kept small: fromCharCode.apply puts the whole chunk on the stack,
  // and polyOp can be reached from deep recursion
  const u16Length = total * 4;
  let key = "";
  for (let i = 0; i < u16Length; i += 2048) {
    const chunk = keyU16.subarray(i, Math.min(i + 2048, u16Length));
    key += String.fromCharCode.apply(null, Array.from(chunk));
  }
  return key;
}

function clonePolygons(polygons: PolyLine2Polygon[]): PolyLine2Polygon[] {
  return polygons.map((polygon) => polygon.map((loop) => new Float64Array(loop)));
}

export function polyOpMemoGet(key: string): PolyLine2Polygon[] | undefined {
  const master = cache.get(key);
  return master === undefined ? undefined : clonePolygons(master);
}

export function polyOpMemoSet(key: string, result: PolyLine2Polygon[]): void {
  cache.add(key, clonePolygons(result));
}

export function polyOpMemoStats(): { hits: number; misses: number; size: number; approxBytes: number } {
  return { hits: cache.hits, misses: cache.misses, size: cache.count, approxBytes: cache.approxBytes };
}

export function polyOpMemoClear(): void {
  cache.clear();
}
